/**
 * Admin step-up proof — JWT claim `stepUpUntil` (epoch **ms**).
 * LOCKED (ADM-TKT-0505): FE respects BE-issued claim; skew ±30s; TTL default 10m on BE.
 * No header alternative. Logout clears via token discard. Refresh must not silently extend.
 */

/** Documented BE default TTL (ms). FE does not mint proofs — only validates claim. */
export const ADMIN_STEP_UP_TTL_MS = 600_000;

/** Clock skew allowance matching BE validate `now <= stepUpUntil + skew`. */
export const ADMIN_STEP_UP_SKEW_MS = 30_000;

export interface AdminJwtStepUpClaims {
  readonly stepUpUntil?: number;
}

/**
 * Display-only decode of admin JWT payload (not signature verification).
 * Mirrors inviteJwt display-only pattern.
 */
export function decodeAdminJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    const payloadB64 = parts[1];
    if (!payloadB64) {
      return null;
    }
    const base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const json = atob(base64 + pad);
    const payload: unknown = JSON.parse(json);
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }
    return payload as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function readStepUpUntilFromToken(token: string | null | undefined): number | null {
  if (token == null || token.length === 0) {
    return null;
  }
  const payload = decodeAdminJwtPayload(token);
  if (payload == null) {
    return null;
  }
  const raw = payload['stepUpUntil'];
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim().length > 0) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Valid when `nowMs <= stepUpUntil + skew` (BE contract).
 * Missing / non-finite claim ⇒ not valid.
 */
export function isAdminStepUpProofValid(
  stepUpUntil: number | null | undefined,
  nowMs: number = Date.now(),
  skewMs: number = ADMIN_STEP_UP_SKEW_MS,
): boolean {
  if (stepUpUntil == null || !Number.isFinite(stepUpUntil)) {
    return false;
  }
  return nowMs <= stepUpUntil + skewMs;
}

export function isAdminStepUpTokenValid(
  token: string | null | undefined,
  nowMs: number = Date.now(),
  skewMs: number = ADMIN_STEP_UP_SKEW_MS,
): boolean {
  return isAdminStepUpProofValid(readStepUpUntilFromToken(token), nowMs, skewMs);
}

/** Known BE error codes for step-up required (FE react without inventing paths). */
const STEP_UP_REQUIRED_CODES = new Set([
  'ADMIN_STEP_UP_REQUIRED',
  'STEP_UP_REQUIRED',
  'STEPUP_REQUIRED',
]);

export function isAdminStepUpRequiredError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') {
    return false;
  }
  const code =
    'code' in error && typeof (error as { code?: unknown }).code === 'string'
      ? (error as { code: string }).code
      : undefined;
  if (code != null && STEP_UP_REQUIRED_CODES.has(code)) {
    return true;
  }
  let message = '';
  if (error instanceof Error) {
    message = error.message;
  } else if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
    message = (error as { message: string }).message;
  }
  return /step[_\s-]?up\s+required/i.test(message);
}
